import React from 'react'
import {
  Button,
  ButtonSentiment,
  buttonStyles,
  ButtonSize,
} from 'sidecar-components/atoms/Button'
import { Icon, IconSize } from 'sidecar-components/atoms/Icon'
import { Toggletip } from 'sidecar-components/atoms/Toggletip'
import { LinkType } from 'sidecar-components/atoms/Link'
import { GenericLinkComponent } from './PreCheckedProviders.types'

interface NoSwipeProps {
  /**
   * Link component to use for external links (Next.js Link, React Router Link,
   * etc.)
   */
  linkComponent?: GenericLinkComponent
  /** Whether the component is used in Premember */
  premember?: boolean
}

export const NoSwipe: React.FC<NoSwipeProps> = ({
  linkComponent,
  premember,
}) => {
  // Icon size mapping for consistent sizing
  const iconSizeMap = {
    [ButtonSize.LARGE]: IconSize.MD,
    [ButtonSize.MEDIUM]: IconSize.SM,
    [ButtonSize.SMALL]: IconSize.XS,
  }

  // Custom link button component that uses the passed linkComponent for external links
  const CustomLinkButton: React.FC<{
    path: string
    pathType?: any
    children: string
    testId?: string
    sentiment?: any
    size?: any
    icon?: string
    iconPosition?: 'before' | 'after'
  }> = ({
    path,
    children,
    testId,
    sentiment,
    size,
    icon,
    iconPosition,
    ...props
  }) => {
    if (linkComponent) {
      // Use the custom link component when provided
      const LinkComponent = linkComponent
      return (
        <LinkComponent
          href={path}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonStyles({
            busy: false,
            children,
            disabled: false,
            icon,
            sentiment,
            size: size || ButtonSize.MEDIUM,
            width: 'flex',
          })}
          data-testid={testId}
        >
          {iconPosition === 'before' && icon && (
            <Icon
              name={icon}
              size={iconSizeMap[size || ButtonSize.MEDIUM] || IconSize.SM}
            />
          )}
          <span className="truncate">{children}</span>
          {iconPosition === 'after' && icon && (
            <Icon
              name={icon}
              size={iconSizeMap[size || ButtonSize.MEDIUM] || IconSize.SM}
            />
          )}
        </LinkComponent>
      )
    }
    // Fall back to the regular Button only when no linkComponent is provided
    return (
      <Button
        path={path}
        pathType={LinkType.EXTERNAL}
        testId={testId}
        sentiment={sentiment}
        size={size}
        icon={icon}
        iconPosition={iconPosition}
        {...props}
      >
        {children}
      </Button>
    )
  }

  return (
    <div className="flex items-center gap-2" data-testid="info_direct_bill">
      <Icon
        name="noSwipe"
        size={IconSize.SM}
        className="color-iconAccent-default"
      />
      <p className="sh-body-sm">
        <strong className="sh-strong">No swipe providers:</strong> Ask these
        providers to bill Sidecar Health. Do not pay them directly.{' '}
        <Toggletip
          content={
            <>
              <p>Do not pay this provider directly.</p>
              <ul className="sh-ul">
                <li>Ask this provider to bill Sidecar Health.</li>
                <li>
                  Show provider your digital member ID so they can bill Sidecar
                  Health.
                </li>
                <li>
                  You won{`\u2019`}t need to collect a medical invoice from this
                  provider.
                </li>
              </ul>
              {!premember && (
                <CustomLinkButton
                  sentiment={ButtonSentiment.SECONDARY_ON_COLOR}
                  path="https://sidecarhealth.com/noswipe"
                  pathType={LinkType.EXTERNAL}
                  testId="btn_learnAboutNoSwipe"
                >
                  Learn more
                </CustomLinkButton>
              )}
            </>
          }
          triggerClassName="color-textPrimary hover:color-textPrimary-hover active:color-textPrimary-active focus:color-textPrimary-hover"
          testId="btn_noSwipe"
        >
          Learn more
        </Toggletip>
      </p>
    </div>
  )
}
